import React, { useState } from 'react';
import { Box, Typography, Paper, IconButton, Button, Checkbox, Menu, MenuItem, Link } from '@mui/material';
import { styled } from '@mui/system';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import { CalendarEventDto } from '../../types/calendar';
import { AddEventButtons } from '../common/AddEventButtons';

const renderWithLinks = (text: string) => {
  const urlRegex = /(https?:\/\/\S+)/g;
  const parts = text.split(urlRegex);

  const shortenUrl = (url: string, maxLength = 30) => {
    if (url.length <= maxLength) {
      return url;
    }
    const start = url.substring(0, 20);
    const end = url.substring(url.length - 7);
    return `${start}...${end}`;
  };

  return (
    <>
      {parts.map((part, index) => {
        if (part.match(urlRegex)) {
          return (
            <Link href={part} target="_blank" rel="noopener noreferrer" key={index} sx={{ wordBreak: 'break-all' }} title={part}>
              {shortenUrl(part)}
            </Link>
          );
        }
        return part;
      })}
    </>
  );
};

const StyledPaper = styled(Paper)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '16px',
  gap: '24px',
  width: '100%',
  background: '#FFFFFF',
  boxShadow: '0px 10px 20px rgba(0, 0, 0, 0.05)',
  borderRadius: '12px',
}));

const TopSection = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: 0,
  gap: '16px',
  width: '100%',
}));

const TitleRow = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: 0,
  width: '100%',
}));

const EventLayout = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  padding: 0,
  gap: '8px',
  width: '100%',
}));

const EventContent = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: 0,
  gap: '1px',
  flexGrow: 1,
}));

interface TaskItemProps {
  event: CalendarEventDto;
  onEdit: (event: CalendarEventDto) => void;
  onDelete: (eventId: number) => void;
  onToggleComplete: (eventId: number, isCompleted: boolean) => void;
}

const TaskItem: React.FC<TaskItemProps> = ({ event, onEdit, onDelete, onToggleComplete }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const canEdit = event.actions.includes('edit');
  const canDelete = event.actions.includes('delete');
  const canComplete = event.actions.includes('complete');

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    onEdit(event);
    handleClose();
  };

  const handleDelete = () => {
    onDelete(event.id);
    handleClose();
  };

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    onToggleComplete(event.id, e.target.checked);
  };

  const formattedTime = new Date(event.scheduledLdt || '').toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });
  const displayText = event.title;

  return (
    <EventLayout>
      <Checkbox checked={event.isCompleted} onChange={handleToggle} disabled={!canComplete} />
      <EventContent>
        <Typography variant="caption" sx={{ color: '#A8A8A8', fontWeight: 600, fontSize: '13px', lineHeight: '16px' }}>
          {formattedTime}
        </Typography>
        <Typography component="div" variant="body2" sx={{ color: '#282828', fontWeight: 700, fontSize: '15px', lineHeight: '24px', wordBreak: 'break-word' }}>
          {renderWithLinks(displayText)}
        </Typography>
      </EventContent>
      {(canEdit || canDelete) && (
        <>
          <IconButton size="small" sx={{ color: '#D3D3D3' }} onClick={handleClick}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
          >
            {canEdit && <MenuItem onClick={handleEdit}>Редагувати</MenuItem>}
            {canDelete && <MenuItem onClick={handleDelete}>Видалити</MenuItem>}
          </Menu>
        </>
      )}
    </EventLayout>
  );
};

interface TasksWidgetProps {
  tasks: CalendarEventDto[];
  onAddTask?: () => void;
  onEditTask: (event: CalendarEventDto) => void;
  onDeleteTask: (eventId: number) => void;
  onToggleTaskComplete: (eventId: number, isCompleted: boolean) => void;
}

export const TasksWidget: React.FC<TasksWidgetProps> = ({ tasks, onAddTask, onEditTask, onDeleteTask, onToggleTaskComplete }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const displayedTasks = isExpanded ? tasks : tasks.slice(0, 3);
  const remainingTasksCount = tasks.length - displayedTasks.length;

  return (
    <StyledPaper>
      <TopSection>
        <TitleRow>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <AssignmentTurnedInIcon sx={{ color: '#41AEE4' }} />
            <Typography variant="h6" sx={{ color: '#282828', fontWeight: 700, fontSize: '15px', lineHeight: '24px' }}>
              Задачі
            </Typography>
          </Box>
        </TitleRow>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
          {displayedTasks.length > 0 ? (
            displayedTasks.map((task) => (
              <TaskItem 
                key={task.id} 
                event={task} 
                onEdit={onEditTask}
                onDelete={onDeleteTask}
                onToggleComplete={onToggleTaskComplete}
              />
            ))
          ) : (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              Поки що немає доданих задач.
            </Typography>
          )}
          {tasks.length > 3 && (
            <Button onClick={() => setIsExpanded(!isExpanded)} sx={{ mt: 1 }}>
              {isExpanded ? 'Показати менше' : `Показати ще ${remainingTasksCount}...`}
            </Button>
          )}
        </Box>
      </TopSection>

      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Typography variant="caption" sx={{ color: '#A8A8A8', fontWeight: 600, fontSize: '13px', lineHeight: '16px' }}>
          Додати задачу:
        </Typography>
        <AddEventButtons
          onAddText={onAddTask}
          showTextButton={true}
          showVoiceButton={false}
          showPhotoButton={false}
          buttonColor="#41AEE4"
        />
      </Box>
    </StyledPaper>
  );
};